import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Backpack, ShoppingBag, Star } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';

interface MarketplaceHeaderProps {
  readonly title: string;
  readonly backPath: string;
}

export function MarketplaceHeader({ title, backPath }: Readonly<MarketplaceHeaderProps>) {
  const [_, setLocation] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [safePaddingTop, setSafePaddingTop] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    const getSafeAreaInsets = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          setSafePaddingTop(50);
          
          const capacitorStatusBar = (window as any).Capacitor?.Plugins?.StatusBar;
          if (capacitorStatusBar?.getInfo) {
            try {
              const info = await capacitorStatusBar.getInfo();
              if (info?.height) {
                setSafePaddingTop(info.height + 5);
              }
            } catch (error) {
              console.log("StatusBar plugin not available", error);
            }
          }
        } catch (error) {
          console.error("Error getting safe area insets:", error);
          setSafePaddingTop(50);
        }
      } else {
        setSafePaddingTop(16);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    getSafeAreaInsets();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Parse the title to ensure consistent styling
  const [firstWord, ...restWords] = title.split(' ');
  const restOfTitle = restWords.join(' ');
  
  return (
    <div 
      className={cn(
        "sticky top-0 z-30 transition-all duration-300",
        scrolled 
          ? "bg-black/90 backdrop-blur-xl shadow-lg" 
          : "bg-gradient-to-b from-black/80 to-transparent"
      )}
      style={{ paddingTop: safePaddingTop ? `${safePaddingTop}px` : undefined }}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation(backPath)}
            className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <h1 className="text-2xl font-bold">
            <span className="text-white">{firstWord}</span>{" "}
            <span className="text-green-500">{restOfTitle}</span>
          </h1>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
            onClick={() => setLocation('/settings')}
          >
            <Backpack className="h-5 w-5" />
          </Button>
          
          
        </div>
      </div>
    </div>
  );
}